import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import WeatherApi from '@/api/config/weatherApi'

import { SRCInterface } from '@/store/types/srcInterfaceType'
import {
  getColumnDetails,
  WeatherReportFields,
} from '@store/slices/WeatherDashboard/types'
import {
  ChartData,
  DataComparisonState,
  SensorDataColumns,
  SensorHistoricDataRequestParams,
  Sensors,
  SensorTypes,
} from '../types/DataComparisonType'

import { staticConvertToMetric } from '@/hooks/useUnits'

const seedWeatherStationDataColumns: SensorDataColumns[] = [
  WeatherReportFields.Humidity,
  WeatherReportFields.ET,
  WeatherReportFields.RainFall,
  WeatherReportFields.WindSpeedAvg,
  WeatherReportFields.THWIndex,
  WeatherReportFields.THSWIndex,
  WeatherReportFields.TemperatureOutsideAvg,
  WeatherReportFields.TemperatureWindchillAvg,
  WeatherReportFields.TemperatureHeatIndexAvg,
  WeatherReportFields.TemperatureDewPointAvg,
  WeatherReportFields.TemperatureWetBulbAvg,
  WeatherReportFields.SolarRadAvg,
]

export const seedSensorData: Sensors[] = [
  {
    id: 8,
    typeId: 1,
    name: 'West Torrens',
    dataColumns: seedWeatherStationDataColumns,
  },
  {
    id: 2,
    typeId: 1,
    name: 'SB3',
    dataColumns: seedWeatherStationDataColumns,
  },
]

export const seedSensorTypes: SensorTypes[] = [
  {
    id: 1,
    name: 'Weather Station',
  },
]

const initialState: DataComparisonState = {
  sensors: {
    isLoading: false,
    data: [],
  },
  sensorTypes: {
    isLoading: false,
    data: [],
  },
  chartDetails: {
    isLoading: false,
    data: [],
  },
  currentCSVData: undefined,
  rightSidebarDetails: {
    variantName: '',
    sensorStateData: [],
    allSensorStateData: [],
    checkedSensorParameter: [],
    openSensor: [],
    sensorType: [],
    inputValue: '',
  },
  timeGrouping: '',
}

export const sensorTypeOrder = [
  'Weather Station',
  'Barometer Sensor',
  'PAR Sensor',
  'Indoor Climate Sensor',
  'Soil Moisture Sensor',
  'pH Sensor',
]

export const fetchSensorHistoricData = createAsyncThunk(
  'weatherDashboard/fetchSensorHistoricData',
  async (params: SensorHistoricDataRequestParams, { rejectWithValue }) => {
    try {
      const allSensorHistoricData: ChartData[] = []

      for (const sensorRequest of params.sensorList) {
        // TODO: Hardcoded Weather Station type for now. to change to dynamic later
        if (sensorRequest.sensorTypeId === 1) {
          const { data } = await WeatherApi.getWeatherReport({
            InterfaceID: params.interfaceId,
            StationId: sensorRequest.sensorId,
            FromDate: params.startDate,
            ToDate: params.endDate,
            Fields: sensorRequest.dataColumns,
          })
          allSensorHistoricData.push(
            ...convertToChartData(
              data,
              sensorRequest.sensorId,
              sensorRequest.sensorName,
            ),
          )
        }
        // Handle other sensor type data fetching here
      }
      return allSensorHistoricData
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

function convertToChartData(
  rawData: any[],
  sensorId: number,
  sensorName: string,
): ChartData[] {
  const nullableKeys = [
    WeatherReportFields.ET.columnName.toLowerCase(),
    WeatherReportFields.RainFall.columnName.toLowerCase(),
  ]
  const chartMap: Record<string, ChartData> = {}
  rawData.forEach((item) => {
    const timestamp = item.timeSpan as string

    Object.entries(item).forEach(([key, value]) => {
      if (key === 'timeSpan') return

      const columnDetail = getColumnDetails(key)
      if (!chartMap[key]) {
        chartMap[key] = {
          sensorId,
          sensorName,
          chartAxisName: columnDetail?.chartAxisName ?? '',
          columnName: columnDetail?.displayName ?? '',
          unitMetric: columnDetail?.metricUnit ?? '',
          unitImperial: columnDetail?.imperialUnit ?? '',
          chartType: columnDetail?.chartType ?? '',
          data: [],
        }
      }

      if (value || value === 0 || nullableKeys.includes(key)) {
        const valueNumber = typeof value === 'number' ? value : Number(value)
        const metricValue = convertValueToMetric(valueNumber, key)
        chartMap[key].data.push({
          timestamp,
          value: metricValue,
        })
      }
    })
  })

  return Object.values(chartMap)
}

function convertValueToMetric(value: number, columnName: string): number {
  const needConversion = [
    WeatherReportFields.TemperatureOutsideAvg.columnName.toLowerCase(),
    WeatherReportFields.ET.columnName.toLowerCase(),
    // WeatherReportFields.RainFall.columnName.toLowerCase(),
    WeatherReportFields.WindSpeedAvg.columnName.toLowerCase(),
    WeatherReportFields.THWIndex.columnName.toLowerCase(),
    WeatherReportFields.THSWIndex.columnName.toLowerCase(),
    WeatherReportFields.TemperatureWindchillAvg.columnName.toLowerCase(),
    WeatherReportFields.TemperatureHeatIndexAvg.columnName.toLowerCase(),
    WeatherReportFields.TemperatureDewPointAvg.columnName.toLowerCase(),
    WeatherReportFields.TemperatureWetBulbAvg.columnName.toLowerCase(),
  ]

  const columnDetail = getColumnDetails(columnName)
  if (!needConversion.includes(columnName.toLowerCase()) || !columnDetail) {
    return value
  }

  return staticConvertToMetric(
    value,
    columnDetail.metricUnit,
    columnDetail.imperialUnit,
  )
}

export const fetchSensorData = createAsyncThunk(
  'dataComparison/fetchSensorData',
  async (interfaceId: string, { rejectWithValue }) => {
    try {
      return seedSensorData as Sensors[]
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchSensorTypes = createAsyncThunk(
  'dataComparison/fetchSensorTypes',
  async (interfaceId: string, { rejectWithValue }) => {
    try {
      return seedSensorTypes as SensorTypes[]
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const dataComparisonSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setupWeatherStationData: (
      state,
      action: PayloadAction<{ currentInterface: SRCInterface }>,
    ) => {
      const extractedSensorData =
        action.payload.currentInterface.weatherLinkStations
          .filter((item) => item.sourceName === 'WeatherLink')
          .map((item) => ({
            id: item.weatherLinkStationId,
            typeId: 1,
            name: item.name,
            dataColumns: seedWeatherStationDataColumns,
          }))
      state.sensors.data = extractedSensorData
    },
    setRightSidePanel: (state, action) => {
      const sensors = [...action.payload.sensorType]

      sensors.sort(
        (a, b) =>
          sensorTypeOrder.indexOf(a.name) - sensorTypeOrder.indexOf(b.name),
      )
      state.rightSidebarDetails = action.payload
      state.rightSidebarDetails.sensorType = sensors
    },
    setCurrentCSVData: (state, action) => {
      state.currentCSVData = action.payload
    },
    setChartLoading: (state, action) => {
      state.chartDetails.isLoading = true
      state.chartDetails.data = []
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchSensorData
      .addCase(fetchSensorData.pending, (state) => {
        state.sensors.isLoading = true
      })
      .addCase(fetchSensorData.fulfilled, (state, action) => {
        state.sensors.isLoading = false
        state.sensors.data = action.payload
      })
      .addCase(fetchSensorData.rejected, (state) => {
        state.sensors.isLoading = false
      })
      // fetchSensorTypes
      .addCase(fetchSensorTypes.pending, (state) => {
        state.sensorTypes.isLoading = true
      })
      .addCase(fetchSensorTypes.fulfilled, (state, action) => {
        state.sensors.isLoading = false
        state.sensorTypes.data = action.payload
      })
      .addCase(fetchSensorTypes.rejected, (state) => {
        state.sensors.isLoading = false
      })
      // fetchSensorHistoricData
      .addCase(fetchSensorHistoricData.pending, (state) => {
        state.chartDetails.isLoading = true
      })
      .addCase(fetchSensorHistoricData.fulfilled, (state, action) => {
        state.chartDetails.isLoading = false
        state.chartDetails.data = action.payload
      })
      .addCase(fetchSensorHistoricData.rejected, (state) => {
        state.chartDetails.isLoading = false
      })
  },
})
// Action creators are generated for each case reducer function
export const {
  setupWeatherStationData,
  setRightSidePanel,
  setCurrentCSVData,
  setChartLoading,
} = dataComparisonSlice.actions

export default dataComparisonSlice.reducer
